'use client';

import { Session } from 'next-auth';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { Accordion, Container, Section, Spacer, SpacerSize, TrilogyColor } from '@bytel/trilogy-react';

import { getDefaultFunding, getPlanPrice, getPlanType } from '@services/products';
import { getSessionVariable } from '@services/storage';

import { AnimcoType } from '@app-types/animco';
import { QUOTE_TYPES } from '@app-types/cart';
import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { FundingType } from '@app-types/funding';
import { SearchParamsType } from '@app-types/page';
import { MobilePlanType } from '@app-types/plan';
import { ProductChildFull, ProductParentFull, ProductType } from '@app-types/product';
import { GALIUM_DATA_STORAGE_KEY } from '@app-types/renewal';
import { UserType } from '@app-types/user';

import { AnimcoBanner } from '@components/animco-banner';
import { ContractSelectionModal } from '@components/contract-selection-modal';
import { FundingLegals } from '@components/funding-legals';
import { Konami } from '@components/konami';
import { Legals } from '@components/legals';
import { EnergyLabelModal, ModalPlanDetails } from '@components/product-details-modal';
import { StructuredData } from '@components/structured-data';
import { TriggerLogin } from '@components/trigger-login';

import { setReadOnlyMarketings, useMarketings } from '@hooks/use-marketings';
import { useOnScreen } from '@hooks/use-on-screen';

import { getCart, getCartPlans } from '@helpers/cart';
import { isDateBetween } from '@helpers/date';
import { replaceWindowUrl, setDocumentTitle } from '@helpers/document';
import { getSessionFunding, hasInstallment, removeSessionFunding, setSessionFunding } from '@helpers/funding';
import { dvdMove } from '@helpers/konami';
import { LegalsSlugType } from '@helpers/partials';
import { paths } from '@helpers/path';
import { getQueryParam } from '@helpers/query-params';
import { getPhoneStructuredData } from '@helpers/structured-data';
import { formatTagText, sendPageViewTracking } from '@helpers/tracking';

import { PlanTypeId } from '@constants/plan';
import { TAG_CATEGORIES } from '@constants/tracking';
import { TradeInStep } from '@constants/trade-in';

import { getOffers } from '@product-detail/actions';
import {
    AnimcoBox,
    BackLink,
    ConnectionBlock,
    Customization,
    MobileTradeInModal,
    ModalStoreAvailability,
    Plans,
    ProductHeader,
    ProductOffersDiscounts,
    ProductPreview,
    ProductSpecifications,
    ProductStickyBox,
    Reinsurance,
    Tabs,
    TabsEnum,
    Ticket,
} from '@product-detail/components';
import {
    PricesDetails,
    getMobilePlans,
    getPricesDetails,
    getProductsStock,
    getSessionTradeInLastStep,
    sendProductPlanPriceRecapTracking,
    sendProductStockTracking,
    setSessionTradeInBonus,
} from '@product-detail/helpers';
import { useFillGallium } from '@product-detail/hooks';
import { ClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

import { Breadcrumb } from './breadcrumb';

export type ProductDetailProps = {
    isRenewal?: boolean;
    baseUrl: string;
    previousPageLabel: string;
    displayedBlocks: {
        offersAndDiscounts: boolean;
        plans: boolean;
    };
    product: ProductParentFull;
    plans?: MobilePlanType[];
    currentPlan?: MobilePlanType;
    currentProduct: ProductChildFull;
    enableKonami?: boolean;
    animco: AnimcoType;
    tradeInBonusPlan?: string;
    searchParams?: SearchParamsType;
    contract?: ContractType;
    userZipCode?: string;
    session?: Session;
    trackingData?: {
        page: string;
        pageCategory: string;
        marketLine: string;
        isB2B?: boolean;
        eCommerceStep?: string;
        variant?: Record<string, string | boolean | undefined>;
        variables?: Record<string, string>;
        products?: Record<string, string | number>[];
    };
    slug: LegalsSlugType;
};

export function ProductDetail({
    isRenewal = false,
    baseUrl,
    previousPageLabel,
    displayedBlocks,
    product,
    plans: _plans = [],
    currentPlan: _currentPlan,
    currentProduct: _currentProduct,
    animco,
    tradeInBonusPlan,
    trackingData,
    searchParams,
    contract,
    userZipCode,
    session,
    enableKonami = false,
    slug,
}: ProductDetailProps) {
    const pathname = usePathname();

    const fundingFromUrl = searchParams ? searchParams.financement?.toUpperCase() : undefined;
    const defaultFunding = _currentPlan
        ? getDefaultFunding(_currentPlan.fundings, fundingFromUrl ?? getSessionFunding())
        : undefined;
    const user = session?.user as UserType | undefined;

    const [hasPlanModalOpen, setHasPlanModalOpen] = useState<boolean>(false);
    const [hasModalStoreAvailabilityOpen, setHasModalStoreAvailabilityOpen] = useState(false);
    const [isEnergyLabelModalOpen, setIsEnergyLabelModalOpen] = useState(false);
    const [hasTradeInModalOpen, setHasTradeInModalOpen] = useState<boolean>(false);

    const [plans, setPlans] = useState<MobilePlanType[]>(_plans);

    const [currentPlanType, setCurrentPlanType] = useState<PlanTypeId>();
    const [currentProduct, setCurrentProduct] = useState<ProductChildFull>(_currentProduct);
    const [currentPlan, setCurrentPlan] = useState<MobilePlanType | undefined>(_currentPlan);
    const [activeTab, setActiveTab] = useState<TabsEnum>(TabsEnum.CHARACTERISTICS);
    const [selectedFunding, setSelectedFunding] = useState<FundingType | undefined>(defaultFunding);

    const [marketings, setMarketings] = useMarketings(currentPlan?.details?.discounts || [], user);

    const defaultPlanPrices = getPlanPrice(marketings, currentPlan);

    const [tradeInStep, setTradeInStep] = useState<TradeInStep>(TradeInStep.NOT_STARTED);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isGalliumLoading, setIsGalliumLoading] = useState<boolean>(isRenewal);

    const [prices, setPrices] = useState<PricesDetails>(
        getPricesDetails(_currentProduct, defaultFunding, defaultPlanPrices),
    );

    const { isVisible: isTicketVisible, ref: ticketRef } = useOnScreen();
    const { isVisible: isStickyVisible, ref: stickyRef } = useOnScreen();

    const galliumData = useFillGallium(setIsGalliumLoading, isRenewal, contract?.id, user);

    const handleSetFunding = (funding?: FundingType) => {
        setSelectedFunding(funding);
        if (funding) {
            setSessionFunding(funding.type);
        } else {
            removeSessionFunding();
        }
    };

    const handleSetCurrentPlan = (plan?: MobilePlanType) => {
        setCurrentPlan(plan);
        if (plan) {
            handleSetFunding(getDefaultFunding(plan.fundings, fundingFromUrl ?? getSessionFunding()));
            if (user) {
                setMarketings({ ...marketings, ...setReadOnlyMarketings(plan.details.discounts ?? []) });
            }
        } else {
            handleSetFunding();
        }
    };

    const getAllPlans = getMobilePlans(handleSetFunding, setPlans, handleSetCurrentPlan, contract, session, !user);

    const hasPlanIsPremium = useMemo(
        () =>
            (currentPlan &&
                ((currentPlan.typeId === PlanTypeId.PREMIUM && !currentPlan.hasLowPrice) ||
                    currentPlan.typeId === PlanTypeId.PREMIUM_RENEWAL)) ??
            false,
        [currentPlan],
    );

    const hasTradeInBonus = useMemo(
        () =>
            (hasPlanIsPremium &&
                product.tradeIn &&
                product.tradeIn.bonus > 0 &&
                isDateBetween(
                    new Date(),
                    new Date(product.tradeIn.dates.start),
                    new Date(product.tradeIn.dates.end),
                )) ??
            false,
        [hasPlanIsPremium],
    );

    const noPlanPrice = useMemo(
        () => getPricesDetails(currentProduct).productPrices.marketingAmount,
        [currentProduct.gencode],
    );

    const onChangeProduct = async (color: string, capacity?: string) => {
        let selectedProduct: ProductChildFull | undefined;
        if (capacity) {
            if (product.featuresRules[color]?.includes(capacity)) {
                selectedProduct = Object.values(product.childs).find(
                    (child) => child.color === color && child.capacity === capacity,
                );
            } else {
                selectedProduct = Object.values(product.childs).find((child) => {
                    const defaultCapacity = product.featuresRules[color]?.[0];
                    return child.color === color && child.capacity === defaultCapacity;
                });
            }
        } else {
            selectedProduct = Object.values(product.childs).find((child) => child.color === color);
        }

        if (!selectedProduct) return;

        refreshStockStatus(selectedProduct).then((newCurrentProduct) => {
            setCurrentProduct(newCurrentProduct);
        });

        if (trackingData) {
            sendPageViewTracking(isRenewal, {
                ...trackingData,
                variables: {
                    stock: formatTagText(selectedProduct.stockDetails.stock.label),
                },
                products: [
                    {
                        name: product.name,
                        id: selectedProduct.entityId,
                        gencode: selectedProduct.gencode,
                        sku: selectedProduct.sku,
                        price: selectedProduct.price!,
                        type_id: selectedProduct.type,
                        manufacturer: product.manufacturer,
                        ligneProduit_niv2: TAG_CATEGORIES[selectedProduct.type]!,
                        color: selectedProduct.color,
                        capacity: selectedProduct.capacity as string,
                    },
                ],
            });
        }
        setDocumentTitle(selectedProduct.title);
        const urlSearchParams = new URLSearchParams(searchParams);
        replaceWindowUrl(
            `${paths.productDetail(product.type, selectedProduct.urlKey, isRenewal)}${urlSearchParams.size > 0 ? `?${urlSearchParams}` : ''}`,
        );
        if (currentPlanType && currentPlan) {
            await getAllPlans(
                selectedProduct.gencode,
                currentPlanType,
                isRenewal,
                currentPlan.gencode,
                isRenewal ? JSON.parse(getSessionVariable(GALIUM_DATA_STORAGE_KEY) || '{}') : undefined,
            );
        }
    };

    const handleInProgressTradeIn = () => {
        const step = getSessionTradeInLastStep();
        if (getQueryParam('recovery') === 'true') {
            setHasTradeInModalOpen(true);
            setTradeInStep(step === TradeInStep.CONFIRMED ? TradeInStep.CONFIRMATION : step);
        } else {
            setTradeInStep(step);
        }
    };

    const handlePlanInUrl = (forfaitInUrl?: string) => {
        const planFromUrl = plans.find((p) => p.urlKey === forfaitInUrl);
        if (planFromUrl || forfaitInUrl === 'sans') {
            handleSetCurrentPlan(planFromUrl);
        }
    };

    const handleOpenTradeInModal = () => {
        if (tradeInStep === TradeInStep.CONFIRMED) {
            setTradeInStep(TradeInStep.CONFIRMATION);
        } else if (tradeInStep === TradeInStep.NOT_STARTED) {
            setTradeInStep(TradeInStep.SEARCH);
        }
        setHasTradeInModalOpen(true);
    };

    const handleOpenAvailability: ClickEvent = (event) => {
        setHasModalStoreAvailabilityOpen(true);
        sendProductStockTracking(isRenewal, (event as Event & { target: HTMLInputElement }).target.innerText);
    };

    const handleOpenPlanModal = () => {
        setHasPlanModalOpen(true);
        sendProductPlanPriceRecapTracking(isRenewal, 'Voir plus de détails');
    };

    useEffect(() => {
        if (trackingData) {
            sendPageViewTracking(isRenewal, trackingData);
        }
        handleSetFunding(defaultFunding);
        handleInProgressTradeIn();
        window.history.scrollRestoration = 'manual';
        refreshStockStatus(currentProduct).then((newCurrentProduct) => {
            setCurrentProduct(newCurrentProduct);
        });
    }, []);

    const refreshStockStatus = async (product: ProductChildFull) => {
        return getProductsStock([product], userZipCode).then((childStock) => {
            const stock = childStock[product.gencode];
            if (stock) {
                const newCurrentProduct = { ...product };
                newCurrentProduct.stockDetails = stock;
                return newCurrentProduct;
            }

            return product;
        });
    };

    const onChangeClientBbox = (isClientBbox: boolean) => {
        if (currentPlan && currentPlanType) {
            getOffers(currentProduct.gencode, currentPlanType, currentPlan.gencode, isClientBbox).then((plans) => {
                handleSetCurrentPlan(plans.find((plan) => plan.gencode === currentPlan.gencode));
            });
        }
    };

    useEffect(() => {
        setSessionTradeInBonus(hasTradeInBonus ? product.tradeIn!.bonus : undefined);
    }, [hasTradeInBonus, tradeInStep]);

    useEffect(() => {
        const quoteContext = isRenewal ? QUOTE_TYPES.RENEWAL : QUOTE_TYPES.ACQUISITION;
        const urlKey = searchParams?.forfait;
        const galliumData = isRenewal ? JSON.parse(getSessionVariable(GALIUM_DATA_STORAGE_KEY) || '{}') : undefined;

        getCart(user, contract?.id).then((cart) => {
            const cartPlan = getCartPlans(cart)?.[0];

            // check if the cart has a plan with the good context
            const cartPlanGencode = cart.cart.quotes.some((quote) => quote.context.id === quoteContext)
                ? cartPlan?.gencode
                : undefined;

            if (cartPlanGencode && currentPlanType) {
                getAllPlans(
                    currentProduct.gencode,
                    currentPlanType,
                    isRenewal,
                    urlKey ? undefined : cartPlanGencode,
                    galliumData,
                ).then(() => setIsLoading(false));
            } else {
                setIsLoading(false);
            }
        });
    }, [currentPlanType]);

    useEffect(() => {
        const forfaitInUrl = searchParams?.forfait;
        handlePlanInUrl(forfaitInUrl);
        setCurrentPlanType(getPlanType(forfaitInUrl, isRenewal, contract?.id));
    }, [searchParams, plans]);

    useEffect(() => {
        setPrices(getPricesDetails(currentProduct, selectedFunding, getPlanPrice(marketings, currentPlan)));
    }, [currentProduct, selectedFunding, marketings, currentPlan]);

    return (
        <Section backgroundColor={TrilogyColor.NEUTRAL_FADE} className='is-paddingless'>
            <TriggerLogin />
            <Section backgroundColor={TrilogyColor.BACKGROUND} className='is-paddingless'>
                <BackLink
                    baseUrl={baseUrl}
                    type={product.type === ProductType.PHONE ? WallType.PHONE : WallType.ACCESSORY}
                    previousPageLabel={previousPageLabel}
                />
            </Section>
            <AnimcoBanner banner={animco.banner[0]} />
            <Spacer size={SpacerSize.SIX} />
            <ProductStickyBox
                currentProduct={currentProduct}
                currentPlan={currentPlan}
                openShopAvailabilityModal={handleOpenAvailability}
                isTicketShowed={isTicketVisible && !isStickyVisible}
                isRenewal={isRenewal}
                user={user}
                prices={prices}
                contract={contract}
                isLoading={isLoading || isGalliumLoading}
                renewalRedirectWall={isRenewal && !galliumData}
                showRef={stickyRef}
            />
            <Section backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <Container medium className='product-page-layout'>
                    <ProductHeader
                        currentProduct={currentProduct}
                        reviews={product.reviews}
                        partnerId={product.partnerId}
                        setActiveTab={setActiveTab}
                    />
                    <ProductPreview
                        features={product.features}
                        currentProduct={currentProduct}
                        setCurrentProduct={onChangeProduct}
                        isRenewal={isRenewal}
                        openEnergyLabelModal={() => setIsEnergyLabelModalOpen(true)}
                    />
                    <div className='product-characteristics'>
                        <Spacer size={SpacerSize.FIVE} />
                        <ProductSpecifications
                            product={currentProduct}
                            setActiveTab={setActiveTab}
                            specificationsCms={product.specificationsCms}
                        />
                        {!isRenewal && product.type === ProductType.PHONE && <ConnectionBlock user={user} />}
                        <AnimcoBox animco={animco.box} />
                        <Customization
                            features={product.features}
                            featuresRules={product.featuresRules}
                            currentProduct={currentProduct}
                            handleProductChange={onChangeProduct}
                            openShopAvailabilityModal={handleOpenAvailability}
                            isRenewal={isRenewal}
                        />
                        {displayedBlocks.plans && plans.length > 0 && (
                            <Plans
                                plans={plans}
                                currentPlan={currentPlan}
                                setCurrentPlan={handleSetCurrentPlan}
                                setMarketings={setMarketings}
                                marketings={marketings}
                                openPlanModal={handleOpenPlanModal}
                                setFunding={handleSetFunding}
                                funding={selectedFunding}
                                isLoading={isLoading || isGalliumLoading}
                                user={user}
                                isRenewal={isRenewal}
                                prices={prices}
                                onChangeClientBbox={onChangeClientBbox}
                                withoutPlanPrice={noPlanPrice}
                            />
                        )}
                        {displayedBlocks.offersAndDiscounts && (
                            <ProductOffersDiscounts
                                product={product}
                                tradeInBonusPlan={tradeInBonusPlan}
                                openTradeInModal={handleOpenTradeInModal}
                                tradeInStep={tradeInStep}
                                setTradeInStep={setTradeInStep}
                                tradeInBonus={hasTradeInBonus ? product.tradeIn!.bonus : 0}
                            />
                        )}
                        <Ticket
                            currentProduct={currentProduct}
                            plan={currentPlan}
                            openPlanModal={handleOpenPlanModal}
                            openShopAvailabilityModal={handleOpenAvailability}
                            tradeInStep={tradeInStep}
                            tradeInBonus={hasTradeInBonus ? product.tradeIn!.bonus : 0}
                            openTradeInModal={handleOpenTradeInModal}
                            selectedFunding={selectedFunding}
                            isLoading={isLoading || isGalliumLoading}
                            showRef={ticketRef}
                            user={user}
                            contract={contract}
                            prices={prices}
                            isRenewal={isRenewal}
                            renewalRedirectWall={isRenewal && !galliumData}
                        />
                    </div>
                </Container>
                <Container medium>
                    <Spacer size={SpacerSize.SIX} />
                    <Reinsurance isRenewal={isRenewal} />
                </Container>
            </Section>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Tabs
                    product={product}
                    currentProduct={currentProduct}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    isRenewal={isRenewal}
                />
            </Section>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Container>
                    <Breadcrumb product={product} isRenewal={isRenewal} />
                    <Accordion>
                        {selectedFunding && hasInstallment(selectedFunding.type) && (
                            <FundingLegals funding={selectedFunding} />
                        )}
                        <Legals id='product-detail-legals' url={slug} isItem />
                    </Accordion>
                </Container>
            </Section>
            {product.type === ProductType.PHONE && hasModalStoreAvailabilityOpen && (
                <ModalStoreAvailability
                    onClose={() => setHasModalStoreAvailabilityOpen(false)}
                    currentProduct={currentProduct}
                    campaignId={product.campaignId ?? ''}
                />
            )}
            {isEnergyLabelModalOpen && currentProduct.characteristics.energyLabel && (
                <EnergyLabelModal
                    onClose={() => setIsEnergyLabelModalOpen(false)}
                    energyLabel={currentProduct.characteristics.energyLabel}
                />
            )}
            <MobileTradeInModal
                isOpen={hasTradeInModalOpen}
                onClose={() => setHasTradeInModalOpen(false)}
                step={tradeInStep}
                setStep={setTradeInStep}
                productName={product.name}
                tradeInBonusPlan={tradeInBonusPlan}
                tradeInBonus={hasTradeInBonus ? product.tradeIn!.bonus : 0}
                contract={contract}
                isRenewal={isRenewal}
            />
            {currentPlan && (
                <ModalPlanDetails
                    plan={currentPlan}
                    price={getPlanPrice(marketings, currentPlan)}
                    isOpen={hasPlanModalOpen}
                    onClose={() => setHasPlanModalOpen(false)}
                    isRenewal={isRenewal}
                />
            )}
            {isRenewal && !contract && <ContractSelectionModal />}
            {enableKonami && (
                <Konami
                    action={() => {
                        dvdMove('productPreview', isRenewal);
                    }}
                />
            )}
            <StructuredData
                data={getPhoneStructuredData(pathname, product, currentProduct, prices.productPrices.todayPrice)}
                data-cy='structured-data'
            />
        </Section>
    );
}
